import { Button, Dialog, Flex, Portal, Separator, Text } from '@chakra-ui/react';
import { PredictionHistoryEntry } from '@features/positions/predictions/types';
import { useHref } from 'hooks/useHref';
import { useRouter } from 'next/navigation';
import { memo, useCallback } from 'react';

interface Props {
  entry: PredictionHistoryEntry | null;
  open: boolean;
  onClose: () => void;
}

const formatTime = (timeStr: string) => {
  const date = new Date(timeStr);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const HistoryDetailDialog = ({ entry, open, onClose }: Props) => {
  const { predictionMarketHref } = useHref();
  const router = useRouter();

  const onClickViewMarket = useCallback(() => {
    if (entry == null) {
      return;
    }
    const href = predictionMarketHref(entry.marketId);
    router.push(href);
    onClose();
  }, [entry, predictionMarketHref, router, onClose]);

  if (entry == null) {
    return null;
  }

  const isPositive = entry.returnValue >= 0;

  return (
    <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()} size="sm">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header px={6} pt={6} pb={0}>
              <Dialog.Title fontSize="lg">{entry.market}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} py={4}>
              <Flex direction="column" gap={4}>
                <Flex direction="column" gap={1}>
                  <Text fontSize="sm" color="fg.subtle" fontWeight="medium">
                    You thought
                  </Text>
                  <Text fontSize="md" fontWeight="semibold">
                    {entry.outcome}
                  </Text>
                </Flex>

                <Separator borderColor="border.light" />

                <Flex gap={6} flexWrap="wrap">
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Activity
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" textTransform="capitalize">
                      {entry.activity}
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Date
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {formatTime(entry.time)}
                    </Text>
                  </Flex>
                </Flex>

                <Separator borderColor="border.light" />

                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                    Return
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color={isPositive ? 'green.600' : 'red.600'}
                  >
                    {isPositive ? '+' : '-'}${Math.abs(entry.returnValue).toFixed(2)}
                  </Text>
                </Flex>
              </Flex>
            </Dialog.Body>
            <Dialog.Footer px={6} pb={6} pt={2} gap={3}>
              <Button variant="outline" size="sm" borderRadius="md" onClick={onClose}>
                Close
              </Button>
              <Button size="sm" borderRadius="md" onClick={onClickViewMarket}>
                View market
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default memo(HistoryDetailDialog);
